import type { StaffFeatureId } from "@wallyo/contracts/actions";
import {
  ALL_DEPARTMENTS,
  labelInWorkspace,
  DEPARTMENTS_BY_ID,
  screensOfDepartment,
  type DepartmentId,
} from "@wallyo/contracts/workspaces";
import { t, type MessageId } from "../i18n";

/** Your own profile, which is not one of the business screens. */
export const PROFILE_ID = "me";

export type ScreenId =
  | "dashboard"
  | "directory"
  | "myRequests"
  | "myTimeOff"
  | "cases"
  | "customer"
  | "verifications"
  | "reports"
  | "payments"
  | "billing"
  | "features"
  | "permissions"
  | "actions"
  | "userSettings"
  | "rules"
  | "hr"
  | "teams"
  | "roles"
  | "audit"
  | "marketplaceOverview"
  | "jobs"
  | "providers"
  | "categories"
  | "marketplaceReviews"
  | "peopleOverview"
  | "financeOverview"
  | "supportOverview"
  | "trustOverview"
  | "adminOverview"
  | "productOverview"
  | "growthOverview"
  | "executive"
  | "incidents"
  | "experiments"
  | "leads"
  | "campaigns"
  | "referrals";

export type Screen = {
  id: ScreenId;
  label: MessageId;
  /** The screen opens with ANY of these. The server checks again on every request. */
  features: readonly StaffFeatureId[];
  /**
   * A workspace's front door (WALLIYO-0132).
   *
   * Not a business screen: it has no feature of its own, shows only what its
   * reader could already open, and is reachable only inside its workspace. So
   * it is listed by `screensOfWorkspace` and left out of `visibleScreens`,
   * which counts the screens somebody was actually given.
   */
  overview?: true;
  /**
   * Marks a screen the team has not worked with yet, so the sidebar says NEW.
   *
   * A fact about the screen rather than about a person: these landed in the last
   * phase and the mark comes off once everybody has used them. Per-person "you
   * have not opened this yet" would need a table, and would mean something else
   * (WALLIYO-0134).
   */
  isNew?: true;
  /**
   * Where this row goes, when it is not simply the screen.
   *
   * A workspace may list a tab of a screen as a row of its own — Human
   * Resources lists Employees, Recruiting and Learning rather than one row
   * called Human Resources, which is what the flat console had. The screen's id
   * stays the screen's, so the feature gate and everything else are unchanged
   * (WALLIYO-0134).
   */
  href?: string;
};

/** The screen → feature map of the approved prototype. */
export const SCREENS: readonly Screen[] = [
  {
    id: "dashboard",
    label: "screen.dashboard",
    features: ["VIEW_HEALTH_DASHBOARD_STAFF_FEATURE_ENABLEMENT"],
  },
  { id: "cases", label: "screen.cases", features: ["CLAIM_CASES_STAFF_FEATURE_ENABLEMENT"] },
  {
    id: "customer",
    label: "screen.customers",
    // Search opens with SEARCH_ACCOUNTS, a customer's page (`#customer/<id>`) with VIEW_ACCOUNTS.
    features: ["SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT", "VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT"],
  },
  {
    id: "verifications",
    label: "screen.verifications",
    features: ["REVIEW_VERIFICATIONS_STAFF_FEATURE_ENABLEMENT"],
  },
  { id: "reports", label: "screen.reports", features: ["HANDLE_REPORTS_STAFF_FEATURE_ENABLEMENT"] },
  { id: "payments", label: "screen.payments", features: ["VIEW_PAYMENTS_STAFF_FEATURE_ENABLEMENT"] },
  // The commercial console (ADR-0018 §4a): the same read feature as Paiements,
  // and each write behind its own (MANAGE_PLAN_PRICES, RUN_PROMOS, OFFER_DISCOUNTS).
  { id: "billing", label: "screen.billing", features: ["VIEW_PAYMENTS_STAFF_FEATURE_ENABLEMENT"] },
  {
    id: "features",
    label: "screen.features",
    features: ["MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT"],
  },
  {
    id: "permissions",
    label: "screen.permissions",
    features: ["MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT"],
  },
  {
    id: "actions",
    label: "screen.actions",
    // A reference table of what the platform can do and what gates each one.
    // Whoever configures features or roles needs to be able to read it.
    features: ["MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT", "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT"],
  },
  {
    id: "userSettings",
    label: "screen.userSettings",
    // Somebody's own choices. Opening the screen needs the same feature as
    // finding them; whether a row can be moved is decided per row, by the
    // action behind each sub-tab.
    features: ["VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT", "EDIT_CUSTOMER_SETTINGS_STAFF_FEATURE_ENABLEMENT"],
  },
  {
    id: "roles",
    label: "screen.roles",
    // Deciding what anybody may do. Critical, and deliberately a higher bar
    // than arranging who works on what (WALLIYO-0131).
    features: ["MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT"],
  },
  {
    id: "teams",
    label: "screen.teams",
    // Arranging who works on what is its own authority: it is not the same as
    // deciding what they may do, which is Permissions (WALLIYO-0131).
    features: ["MANAGE_TEAMS_STAFF_FEATURE_ENABLEMENT"],
  },
  {
    id: "hr",
    label: "screen.hr",
    // No features: the screen opens for anybody who can sign in, because its
    // first tab is the directory. Gating that would not protect anything — org
    // structure is visible from walking across the office — while making the
    // console worse at the one question everybody has. The half that is HR's
    // (Recruiting, Employees and the seven sub-tabs) is gated on the tab, by
    // MANAGE_HUMAN_RESOURCES, and again on every endpoint underneath.
    features: [],
  },
  // ── Every workspace's first screen (WALLIYO-0132) ──────────────────────
  // No feature of their own: an Overview shows only what its reader could
  // already open, and each figure is taken behind that screen's own feature.
  { id: "peopleOverview", label: "screen.overview", features: [], overview: true },
  { id: "financeOverview", label: "screen.overview", features: [], overview: true },
  { id: "supportOverview", label: "screen.overview", features: [], overview: true },
  { id: "trustOverview", label: "screen.overview", features: [], overview: true },
  { id: "adminOverview", label: "screen.overview", features: [], overview: true },
  { id: "productOverview", label: "screen.overview", features: [], overview: true },
  { id: "growthOverview", label: "screen.overview", features: [], overview: true },
  { id: "executive", label: "screen.overview", features: [], overview: true },
  // Yours, in every workspace: what you asked for, and the directory. Gating
  // either would protect nothing — you may always see what you yourself asked.
  // Who works here. Its own screen rather than a link into a tab of Human
  // Resources: every workspace's sidebar lists it, and a row in that block must
  // belong to all of them or following it moves you to somebody else's
  // workspace. Nothing gates it — org structure is visible from walking across
  // the office.
  { id: "directory", label: "screen.as.directory", features: [] },
  { id: "myRequests", label: "screen.myRequests", features: [] },
  { id: "myTimeOff", label: "screen.myTimeOff", features: [] },
  {
    id: "experiments",
    label: "screen.experiments",
    features: ["MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT"],
    isNew: true,
  },
  { id: "leads", label: "screen.leads", features: ["MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT"], isNew: true },
  { id: "campaigns", label: "screen.campaigns", features: ["MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT"], isNew: true },
  { id: "referrals", label: "screen.referrals", features: ["MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT"], isNew: true },
  // Incidents (WALLIYO-0132, #126): opening one puts a banner in front of
  // every customer, so it sits behind the same feature as the app's features.
  {
    id: "incidents",
    label: "screen.incidents",
    features: ["MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT"],
    isNew: true,
  },
  // ── Marketplace Operations (WALLIYO-0132) ──────────────────────────────
  // Reads over `jobs`, `job_quotes`, `workers`, `worker_skills` and
  // `reviews`, which had no screen anywhere until now.
  {
    id: "marketplaceOverview",
    label: "screen.marketplaceOverview",
    // Unlike the other Overviews this one is gated, because it is the market's
    // own numbers rather than a summary of screens you already hold. Being a
    // front door and needing a feature are two different things.
    features: ["VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT"],
    overview: true,
  },
  { id: "jobs", label: "screen.jobs", features: ["VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT"] },
  { id: "providers", label: "screen.providers", features: ["VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT"] },
  { id: "categories", label: "screen.categories", features: ["VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT"] },
  {
    id: "marketplaceReviews",
    label: "screen.marketplaceReviews",
    features: ["VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT"],
  },
  { id: "rules", label: "screen.rules", features: ["EDIT_RULES_STAFF_FEATURE_ENABLEMENT"] },
  { id: "audit", label: "screen.audit", features: ["VIEW_AUDIT_LOG_STAFF_FEATURE_ENABLEMENT"] },
];

export function canOpen(screen: Screen, features: readonly StaffFeatureId[]): boolean {
  // A screen naming no feature is open to anybody who can sign in. Today that
  // is Human Resources, which gates its own tabs instead (WALLIYO-0131).
  if (screen.features.length === 0) return true;
  return screen.features.some((feature) => features.includes(feature));
}

/**
 * Every business screen this person can open, anywhere in the console.
 *
 * Workspace front doors are left out: they gate on nothing, so counting them
 * would tell somebody they had been given eight screens they were not.
 */
export function visibleScreens(features: readonly StaffFeatureId[]): Screen[] {
  return SCREENS.filter((screen) => !screen.overview && canOpen(screen, features));
}

// ── Workspaces (WALLIYO-0132) ────────────────────────────────────────────

/**
 * The screens every employee gets, in every workspace.
 *
 * Not a department: leave, the directory and what you have asked for belong to
 * the person, not to where they sit. For most staff this block is the console.
 */
/**
 * The screens every employee gets, in every workspace, in the order the design
 * lists them: your leave, then what you have asked to be allowed to do.
 */
export const UNIVERSAL_SCREEN_IDS: readonly ScreenId[] = ["directory", "myTimeOff", "myRequests"];

const BY_ID = new Map(SCREENS.map((screen) => [screen.id as string, screen]));

/**
 * A workspace's screens, filtered to the ones this person can open.
 *
 * The department's list is a superset, not a promise: a product manager and an
 * engineer share one workspace and are offered different halves of it. An id
 * the console has not built yet is skipped, so the catalogue can name a screen
 * before it exists.
 */
export function screensOfWorkspace(id: DepartmentId, features: readonly StaffFeatureId[]): Screen[] {
  return screensOfDepartment(id)
    .flatMap((listed) => {
      // `hr/employees` is one row pointing into a tab of the HR screen. The part
      // before the slash is the screen, so gating and routing are unchanged.
      const [screenId] = listed.split("/");
      const screen = BY_ID.get(screenId ?? listed);
      if (!screen) return [];
      // Under this workspace's own name for it, where it has one: the screen is
      // the same screen, and "Money" is what an Executive calls Payments.
      const named = labelInWorkspace(id, listed);
      return [
        {
          ...screen,
          ...(named ? { label: named as Screen["label"] } : {}),
          ...(listed === screenId ? {} : { href: `#${listed}` }),
        },
      ];
    })
    .filter((screen) => canOpen(screen, features));
}

/**
 * The workspaces somebody actually has, in department order.
 *
 * Two conditions, and both matter: a role of theirs names the department, and
 * the department has at least one screen they can open. A role that names a
 * department whose screens they cannot open offers an empty workspace, which
 * is worse than not offering it.
 */
/**
 * Somebody who holds every feature the console gates a screen on.
 *
 * That is Owner: the role that exists in order to be able to do everything. It
 * is the one case where a person reaches a workspace no role of theirs names,
 * and the design has always said so — an Executive's whole Overview is one line
 * per department with a link into theirs, and those links have to arrive
 * somewhere honest.
 *
 * **This is not deriving workspaces from features.** An Engineer holding
 * `VIEW_ACCOUNTS`, `VIEW_PAYMENTS` and `VIEW_AUDIT_LOG` still gets the one
 * workspace their role names, which is the entire reason the department is a
 * column on the role rather than a guess about capabilities. Holding *all* of
 * them is a different fact about a person: no workspace has anything to keep
 * from them.
 */
export function holdsEverything(features: readonly StaffFeatureId[]): boolean {
  const held = new Set<string>(features);
  return SCREENS.every((screen) => screen.features.every((feature) => held.has(feature)));
}

export function workspacesOf(
  departments: readonly (DepartmentId | null)[],
  features: readonly StaffFeatureId[],
): DepartmentId[] {
  const named = new Set(departments.filter((id): id is DepartmentId => id !== null));
  const everywhere = holdsEverything(features);
  return ALL_DEPARTMENTS.filter(
    (department) =>
      department.hasWorkspace &&
      (everywhere || named.has(department.id)) &&
      // A business screen, not just the front door: a workspace whose only
      // page is an Overview with nothing on it is worse than not offering it.
      screensOfWorkspace(department.id, features).some((screen) => !screen.overview),
  ).map((department) => department.id);
}

/**
 * A workspace's own Overview: the screen somebody lands on.
 *
 * `visibleScreens` leaves Overviews out, so they never duplicate in the
 * sidebar's list — which also meant nothing could find them. This is the way
 * back in, and it is the only one: the front door is a property of the
 * workspace, not something a route guesses.
 */
export function overviewOfWorkspace(id: DepartmentId, features: readonly StaffFeatureId[]): Screen | null {
  return screensOfWorkspace(id, features).find((screen) => screen.overview === true) ?? null;
}

/**
 * Where somebody lands: the first workspace their roles open, in department
 * order. `resolveRoute`'s old "first screen you can open" rule, lifted one
 * level, so there is no preference to store.
 */
export function landingWorkspace(
  departments: readonly (DepartmentId | null)[],
  features: readonly StaffFeatureId[],
): DepartmentId | null {
  return workspacesOf(departments, features)[0] ?? null;
}

/**
 * Which workspace is showing a screen.
 *
 * A screen can be listed in more than one — Payments is in Finance and, for
 * debugging, in Product & Engineering. Staying put is the right answer when
 * the current workspace already lists it; otherwise the first of theirs that
 * does.
 */
export function workspaceShowing(
  screen: Screen,
  current: DepartmentId | null,
  available: readonly DepartmentId[],
  features: readonly StaffFeatureId[],
): DepartmentId | null {
  const lists = (id: DepartmentId) => screensOfWorkspace(id, features).some((each) => each.id === screen.id);
  if (current && lists(current)) return current;
  return available.find(lists) ?? current;
}

/**
 * What the sidebar lists.
 *
 * With a workspace, its screens. Without one — nobody's roles name a
 * department, which is every role until somebody sets one — everything they
 * can open, which is exactly how the console behaved before workspaces. A
 * role with no department must never be a role that locks you out.
 */
export function sidebarScreens(workspace: DepartmentId | null, features: readonly StaffFeatureId[]): Screen[] {
  return workspace ? screensOfWorkspace(workspace, features) : visibleScreens(features);
}

/** What a workspace is called, for the header and the switcher. */
export function workspaceLabel(id: DepartmentId): string {
  return t(`department.${id}` as MessageId);
}

/** One line on what a workspace is for, under its name in the switcher. */
export function workspaceBlurb(id: DepartmentId): string {
  return t(`department.${id}.blurb` as MessageId);
}

/** True while a department exists on the org chart with nothing to open. */
export function hasWorkspace(id: DepartmentId): boolean {
  return DEPARTMENTS_BY_ID[id].hasWorkspace;
}

export type Route =
  | { kind: "screen"; screen: Screen }
  | { kind: "locked"; screen: Screen }
  /**
   * Your own profile. Not a `Screen`: the eleven screens are the business and
   * each is gated on a feature, while this is about the person and everyone
   * who can sign in can open it. Giving it a feature would mean a staff
   * member could be locked out of their own name.
   */
  | { kind: "profile" }
  /** Nothing asked for, and nothing this person can open. */
  | { kind: "empty" };

/**
 * What a URL hash opens. A known screen without its feature is `locked`
 * (shown with the lock message, never in the menu). An unknown or empty hash
 * falls back to the first screen this person can open. Anything after `/` or
 * `?` is the screen's own state (a customer's id, the audit log's filters) and
 * doesn't pick the screen.
 */
export function resolveRoute(
  hash: string,
  features: readonly StaffFeatureId[],
  /** The workspace they land in, so an empty hash opens its Overview. */
  landing: DepartmentId | null = null,
): Route {
  const id = hash.replace(/^#\/?/, "").split(/[/?]/)[0];
  if (id === PROFILE_ID) {
    return { kind: "profile" };
  }
  const asked = SCREENS.find((screen) => screen.id === id);
  if (asked) return canOpen(asked, features) ? { kind: "screen", screen: asked } : { kind: "locked", screen: asked };

  // No hash: the front door of their workspace, which is what the design is.
  // Falling through to the first business screen is how everybody arrived in
  // the flat console while nine Overviews sat unreachable (WALLIYO-0134).
  const front = landing ? overviewOfWorkspace(landing, features) : null;
  if (front) return { kind: "screen", screen: front };

  const first = visibleScreens(features)[0];
  return first ? { kind: "screen", screen: first } : { kind: "empty" };
}

export function screenHref(id: ScreenId): string {
  return `#${id}`;
}

/**
 * Whether this workspace lists that screen's tabs as sidebar rows of its own.
 *
 * When it does, the screen must not draw its own tab strip: the sidebar is
 * already that navigation, and showing both puts Employees, Recruiting and
 * Learning on the page twice (WALLIYO-0134).
 *
 * Human Resources lists them; Executive lists the same screen as one row called
 * Workforce, and there the strip is the only way to move inside it — so this is
 * a question about the workspace, not about the screen.
 */
export function tabsAreRows(workspace: DepartmentId | null, screenId: ScreenId): boolean {
  if (!workspace) return false;
  return screensOfDepartment(workspace).some((listed) => listed.startsWith(`${screenId}/`));
}

export function profileHref(): string {
  return `#${PROFILE_ID}`;
}

export type LockMessage = {
  /** The full feature IDs, any one of which would open the screen. */
  features: readonly StaffFeatureId[];
  /** e.g. "Cet écran nécessite" — the IDs follow, joined by `or`. */
  requires: string;
  or: string;
  /** Why it isn't in the menu, naming the person's roles. */
  roles: string;
};

/** The prototype's lock message for a screen opened without its feature. */
export function lockMessage(screen: Screen, roleNames: readonly string[]): LockMessage {
  return {
    features: screen.features,
    requires: t("lock.requires"),
    or: t("lock.or"),
    roles: roleNames.length > 0 ? t("lock.roles", { roles: roleNames.join(", ") }) : t("lock.noRoles"),
  };
}

/** The lock message as one plain sentence, e.g. for a title attribute or a test. */
export function lockMessageText(message: LockMessage): string {
  return `${message.requires} ${message.features.join(message.or)}. ${message.roles}`;
}
